import React, { useState } from 'react'
import { Globe, ChevronDown, Check } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, type SupportedLanguage } from '../lib/i18n'
import { useCurrentLang } from '../lib/hooks/useCurrentLang'

// SVG flag components for consistent rendering across all platforms
const FlagIcons: Record<SupportedLanguage, React.FC<{ className?: string }>> = {
  en: ({ className = '' }) => (
    <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
      <path fill="#bd3d44" d="M0 0h640v480H0z"/>
      <path fill="#fff" d="M0 37h640v73H0zm0 146h640v73H0zm0 146h640v73H0z"/>
      <path fill="#192f5d" d="M0 0h247v274H0z"/>
      <g fill="#fff">
        <path d="m21 16 5 17h-19l16-12 6 18-16-12-16 12 6-18-17-12h21z"/>
        <path d="m68 16 5 17h-19l16-12 6 18-16-12-16 12 6-18-17-12h21z"/>
        <path d="m21 62 5 18h-19l16-12 6 18-16-12-16 12 6-18-17-12h21z"/>
        <path d="m68 62 5 18h-19l16-12 6 18-16-12-16 12 6-18-17-12h21z"/>
        <path d="m45 39 5 18h-19l16-12 6 18-16-12-16 12 6-18-17-12h21z"/>
      </g>
    </svg>
  ),
  de: ({ className = '' }) => (
    <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
      <path fill="#000" d="M0 0h640v160H0z"/>
      <path fill="#d00" d="M0 160h640v160H0z"/>
      <path fill="#ffce00" d="M0 320h640v160H0z"/>
    </svg>
  ),
  fr: ({ className = '' }) => (
    <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
      <g fill-rule="evenodd" stroke-width="1pt">
        <path fill="#fff" d="M0 0h640v480H0z"/>
        <path fill="#00267f" d="M0 0h213v480H0z"/>
        <path fill="#f31830" d="M427 0h213v480H427z"/>
      </g>
    </svg>
  ),
  es: ({ className = '' }) => (
    <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
      <path fill="#aa151b" d="M0 0h640v480H0z"/>
      <path fill="#f1bf00" d="M0 120h640v240H0z"/>
      <path fill="#aa151b" d="M0 0h640v480H0z"/>
    </svg>
  ),
  pt: ({ className = '' }) => (
    <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
      <path fill="#ff0000" d="M0 0h640v480H0z"/>
      <path fill="#006600" d="M0 0h320v480H0z"/>
      <path fill="#ffff00" d="M248 189a55 55 0 1 0 24 101 80 80 0 1 1-24-101z"/>
    </svg>
  ),
  hi: ({ className = '' }) => (
    <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
      <path fill="#fff" d="M0 0h640v480H0z"/>
      <path fill="#f93" d="M0 160h640v160H0z"/>
      <path fill="#128807" d="M0 320h640v160H0z"/>
      <g transform="translate(320 240)">
        <circle r="48" fill="#008"/>
        <g fill="#fff">
          <circle cy="-16" r="6"/>
          <circle cy="12" r="6"/>
          <path d="M0-26v6l-16-4v6l16 4v6l-16 4v6l16-4v6l16-4v-6l-16-4v-6l16-4v-6l-16 4v-6z"/>
        </g>
      </g>
    </svg>
  ),
  zh: ({ className = '' }) => (
    <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
      <path fill="#de2910" d="M0 0h640v480H0z"/>
      <g fill="#ffde00">
        <path d="m119 78 14 44h46l-37 27 14 45-37-27-37 27 14-45-37-27h46z"/>
        <path d="m204 24 6 19h19l-15 11 6 20-16-11-16 11 6-20-15-11h19z"/>
        <path d="m249 72 6 19h20l-16 11 6 19-16-11-16 11 6-19-16-11h20z"/>
        <path d="m249 132 6 19h20l-16 11 6 19-16-11-16 11 6-19-16-11h20z"/>
        <path d="m204 176 6 19h19l-15 11 6 20-16-11-16 11 6-20-15-11h19z"/>
      </g>
    </svg>
  ),
}

export function LanguageSwitcher() {
  const [isOpen, setIsOpen] = useState(false)
  // Get language from URL (which reflects the actual current language)
  const currentLanguage = (useCurrentLang() as SupportedLanguage) || 'en'

  const handleLanguageChange = async (lang: SupportedLanguage) => {
    try {
      // Update URL to include language prefix
      const currentPath = window.location.pathname
      // Remove the current language prefix if it exists
      const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}/, '') || '/'
      // Ensure the path starts with /
      const cleanPath = pathWithoutLang.startsWith('/') ? pathWithoutLang : `/${pathWithoutLang}`
      const newPath = `/${lang}${cleanPath}`

      // Store in localStorage AFTER computing the new path (ensures it's set before reload)
      localStorage.setItem('i18nextLng', lang)

      // Force localStorage to sync (important for some browsers)
      localStorage.setItem('i18nextLng_sync', Date.now().toString())
      localStorage.removeItem('i18nextLng_sync')

      // Small delay to ensure localStorage is written
      await new Promise(resolve => setTimeout(resolve, 100))

      // Navigate to new path (this will trigger a reload and use the new language)
      window.location.href = newPath
    } catch (error) {
      console.error('Failed to change language:', error)
    }
  }

  return (
    <div className="relative">
      {/* Language Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="relative inline-flex items-center justify-center gap-2 h-10 px-3 rounded-md border border-border bg-background transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        aria-label="Switch language"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <span className="h-5 w-7 overflow-hidden rounded-sm" aria-label={LANGUAGE_NAMES[currentLanguage]}>
          {React.createElement(FlagIcons[currentLanguage], { className: 'h-full w-full object-cover' })}
        </span>
        <ChevronDown
          size={16}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </motion.button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Menu */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="fixed md:absolute left-1/2 md:left-auto -translate-x-1/2 md:translate-x-0 md:right-0 top-auto md:mt-2 mt-16 w-56 bg-card rounded-lg shadow-lg border border-border py-1 z-50"
            >
              <div className="px-3 py-2 border-b border-border">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Globe size={16} />
                  <span>Select Language</span>
                </div>
              </div>

              {SUPPORTED_LANGUAGES.map((lang) => {
                const isSelected = lang === currentLanguage
                const FlagIcon = FlagIcons[lang]
                return (
                  <motion.button
                    key={lang}
                    onClick={() => handleLanguageChange(lang)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${
                      isSelected
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground hover:bg-muted'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="h-5 w-7 overflow-hidden rounded-sm shrink-0" aria-label={LANGUAGE_NAMES[lang]}>
                        <FlagIcon className="h-full w-full object-cover" />
                      </span>
                      <span>{LANGUAGE_NAMES[lang]}</span>
                    </div>
                    {isSelected && <Check size={16} className="text-primary" />}
                  </motion.button>
                )
              })}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
